import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import "react-loading-skeleton/dist/skeleton.css";
import styles from './index.module.css';
import TableSkeleton from "../../../../components/Skeleton";
import Pagination from "../../../../components/Pagination";
import NoResultsFound from "../../../../components/NoResultsFound";
import Modal from "../../../../components/Modal";
import { faCircleCheck, faCircleXmark, faDownload, faXmark } from "@fortawesome/free-solid-svg-icons";
import { editCertificate, getAllCertificates, getAllCertificatesToBulkDownload } from "../../../../redux/features/certificate/certificateService";
import { getAllActiveCourses } from "../../../../redux/features/course/courseService";
import FilterDropdown from "../../../../components/FilterDropdown";
import SearchBarRow from "../../../../components/SearchBarRow";
import Certificate from "../../../../components/Certificate";
import { pdf } from "@react-pdf/renderer";
import QRCode from "qrcode";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse } from "date-fns";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const QR_Link = process.env.REACT_APP_PUBLIC_QR_LINK;

const certificateStatus = [
    { id: '1', name: 'Pending' },
    { id: '2', name: 'Approved' },
    { id: '3', name: 'Printed' },
    { id: '4', name: 'Issued' }];

const convertToDate = (dateString) => {
    // Remove ordinal suffixes (st, nd, rd, th)
    const cleanedDate = dateString.replace(/(\d+)(st|nd|rd|th)/, "$1");

    // Convert string to Date object
    return parse(cleanedDate, "d MMMM yyyy", new Date());
};

const CertificateList = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const courseDropdownRef = useRef(null);
    const statusDropdownRef = useRef(null);
    const editStatusDropdownRef = useRef(null);

    const { isCertificatesLoading, pageCount, certificates } = useSelector((state) => state.certificate.allCertificates);
    const { activeCourses } = useSelector((state) => state.course.allActiveCourses);
    const { isEditingCertificate } = useSelector((state) => state.certificate.editedCertificate);

    const [searchQuery, setSearchQuery] = useState('');

    const [selectedCountryId, setSelectedCountryId] = useState(null);
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState({
        id: '',
        name: ''
    });
    const [selectedStatus, setSelectedStatus] = useState({
        id: '',
        name: ''
    });
    const [selectedEditStatus, setSelectedEditStatus] = useState({
        id: '',
        name: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingCertificateId, setLoadingCertificateId] = useState(null);

    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCertificate, setSelectedCertificate] = useState(null);
    const [initialCertificate, setInitialCertificate] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        course_code: ''
    });
    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const [completionDate, setCompleteDate] = useState(new Date());
    const [certificateDate, setCertificateDate] = useState(new Date());
    const [selectedCertificateId, setSelectedCertificateId] = useState(null);
    const [isBulkGenerating, setBulkGenerating] = useState(false);
    const [higherStatuses, setHigherStatuses] = useState([]);

    const fetchAllCourses = useCallback(() => {
        dispatch(getAllActiveCourses()).unwrap();
    }, [dispatch]);


    const fetchAllCertificates = useCallback(
        (filters = {}) => {
            const params = {
                search: searchQuery,
                course_code: selectedCourse.id,
                skip: currentPage - 1,
                limit: 10,
                status: selectedStatus.name,
                ...filters,
            };
            dispatch(getAllCertificates(params));
        },
        [dispatch, searchQuery, selectedStatus, selectedCourse, currentPage]
    );

    const fetchBulkCertificates = useCallback(
        (filters = {}) => {
            const params = {
                course_code: selectedCourse.id,
                status: selectedStatus.name,
                ...filters,
            };
            dispatch(getAllCertificatesToBulkDownload(params));
        },
        [dispatch, selectedStatus, selectedCourse]
    );


    useEffect(() => {
        fetchAllCourses();
        fetchAllCertificates();
    }, [fetchAllCertificates, fetchAllCourses]);

    const handleSelect = (option) => {
        setSelectedCourse({
            id: option.id,
            name: option.name
        });
        setCurrentPage(1);
    };

    const handleSelectStatus = (option) => {
        setSelectedStatus({
            id: option.id,
            name: option.name
        });
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editCertificate({ id: selectedCountryId, status: true })).unwrap();
            setSelectedCountryId(null);
            showToast('success', response.message);
            fetchAllCertificates();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editCertificate({ id: selectedCountryId, status: false })).unwrap();
            setSelectedCountryId(null);
            showToast('success', response.message);
            fetchAllCertificates();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // const previewCertificate = async (certificate) => {
    //     navigate("/admin/preview-certificate", { state: { certificate } })
    // }

    const previewCertificate = async (certificate) => {
        navigate(`/controller/certificates/preview-certificate/${certificate.id}`)
    }

    const openCertificate = async (certificate) => {
        try {
            setLoadingCertificateId(certificate.id);
            const qrDataUrl = await QRCode.toDataURL(
                `${QR_Link}/document_ref/${certificate.document_ref}`,
                { width: 100 }
            );
            const pdfBlob = await pdf(<Certificate data={[certificate, qrDataUrl]} />).toBlob();
            const pdfUrl = URL.createObjectURL(pdfBlob);
            window.open(pdfUrl, "_blank");
        } catch (error) {
            showToast(
                "error",
                "An error occurred while generating the PDF. Please try again."
            );
        } finally {
            setLoadingCertificateId(null);
        }
    }

    // Edit certificate
    const handleSelectedCertificateChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedCertificate((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditCertificate = () => {
        const errors = {};
        const trimmedName = selectedCertificate.student_name.trim();

        if (!trimmedName) {
            errors.student_name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData, completeDate, certificateDate) {
        const changedValues = {};
        const keysToCheck = ['student_name', 'completion_date', 'certificate_date', 'certificate_status'];

        for (const key of keysToCheck) {
            let newValue = selectedData[key];

            // Use the provided completeDate and certificateDate if checking these fields
            if (key === 'completion_date' && completeDate !== initialCertificate[key]) {
                changedValues[key] = completeDate;
            } if (key === 'certificate_date' && certificateDate !== initialCertificate[key]) {
                changedValues[key] = certificateDate;
            } if (key === 'student_name' && newValue !== initialCertificate[key]) {
                changedValues[key] = newValue;
            }
        }

        // Ensure `id` is always included
        if (selectedData.id) {
            changedValues.id = selectedData.id;
        }

        return changedValues;
    }

    const formatDateWithSuffix = (date) => {
        const day = date.getDate();
        const month = date.toLocaleString("en-US", { month: "long" });
        const year = date.getFullYear();

        // Function to add ordinal suffix
        const getOrdinalSuffix = (num) => {
            if (num > 3 && num < 21) return "th"; // 11th, 12th, 13th...
            switch (num % 10) {
                case 1: return "st"; // 1st
                case 2: return "nd"; // 2nd
                case 3: return "rd"; // 3rd
                default: return "th"; // 4th, 5th, 6th...
            }
        };

        return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    };

    const handleSelectEditStatus = (option) => {
        setSelectedEditStatus({
            id: option.id,
            name: option.name
        });
    };

    const editCertificateDetails = async () => {
        const completeData = formatDateWithSuffix(completionDate);
        if (selectedCertificate.completion_date !== completeData) {
            setSelectedCertificate(prevState => ({
                ...prevState,
                completion_date: completeData
            }));
        }
        const certificateData = formatDateWithSuffix(certificateDate);
        if (selectedCertificate.certificate_date !== certificateData) {
            setSelectedCertificate(prevState => ({
                ...prevState,
                certificate_date: certificateData
            }));
        }
        if (isValidEditCertificate(selectedCertificate.student_name)) {
            const data = getChangedValues(selectedCertificate, completeData, certificateData);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editCertificate(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedCertificate(null);
                    setInitialCertificate(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllCertificates();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    const editCertificateStatus = async () => {
        setEditBtnDisable(true);
        try {
            const response = await dispatch(editCertificate({ id: selectedCertificateId, certificate_status: selectedEditStatus.name })).unwrap();
            setEditBtnDisable(false);
            setSelectedCertificate(null);
            setInitialCertificate(null);
            setPrintModalOpen(false);
            showToast('success', response.message);
            fetchAllCertificates();
        }
        catch (error) {
            setEditBtnDisable(false);
            showToast('error', error.message);
        };
    };
    //Certificate status
    const [printModalOpen, setPrintModalOpen] = useState(false);

    const handleClearFilters = () => {
        if (courseDropdownRef.current) {
            courseDropdownRef.current.clearSelection();
        }
        if (statusDropdownRef.current) {
            statusDropdownRef.current.clearSelection();
        }
        setSelectedCourse({ id: '', name: '' });
        setSelectedStatus({ id: '', name: '' });
        setSearchQuery("");
    };

    const downloadBulk = async () => {
        setBulkGenerating(true);
        const params = {
            course_code: selectedCourse.id,
            status: selectedStatus.name,
        };
        try {
            const response = await dispatch(getAllCertificatesToBulkDownload(params)).unwrap();
            const certificates = response.data;

            if (!certificates.length) {
                return;
            }

            const zip = new JSZip();
            const pdfPromises = certificates.map(async (cert) => {
                const qrDataUrl = await QRCode.toDataURL(
                    `${QR_Link}/document_ref/${cert.document_ref}`,
                    { width: 100 }
                );
                const pdfBlob = await pdf(<Certificate data={[cert, qrDataUrl]} />).toBlob();
                zip.file(`Certificate_${cert.document_ref}.pdf`, pdfBlob);
            });

            await Promise.all(pdfPromises);
            zip.generateAsync({ type: "blob" }).then((zipBlob) => {
                saveAs(zipBlob, "Certificates.zip");
            });
            if (selectedStatus.name === 'Approved') {
                await Promise.all(certificates.map(cert => 
                    dispatch(editCertificate({ id: cert.id, certificate_status: 'Printed' })).unwrap()
                ));
            }            
            fetchAllCertificates();
            setBulkGenerating(false);
        } catch (error) {
            showToast('error', error.message);
            setBulkGenerating(false);
        }
    }


    const handleChangeStatusClick = (certificate) => {
        setSelectedCertificateId(certificate.id);

        const currentStatus = certificateStatus.find(status => status.name === certificate.certificate_status);

        if (currentStatus) {
            setSelectedEditStatus(currentStatus);

            // Filter only higher order statuses
            const filteredStatuses = certificateStatus.filter(
                status => parseInt(status.id) > parseInt(currentStatus.id)
            );

            setHigherStatuses(filteredStatuses);
        } else {
            setHigherStatuses([]);
        }

        setPrintModalOpen(true);
    };

    return (
        <div className={`search-card`}>
            <div className={`search-card-header`}>
                <h1 className={`form-title`}>Certificate List</h1>
            </div>
            <div className={styles.searchSec}>
                <div className={`form-input-row ${styles.rowFullWidth}`}>
                    <div className={`form-input-group`}>
                        <FilterDropdown
                            ref={courseDropdownRef}
                            options={activeCourses.map(course => ({
                                ...activeCourses,
                                id: course.code,
                                name: course.name
                            }))}
                            placeholder="Choose a Course"
                            onSelect={handleSelect}
                            value={selectedCourse.name}
                        />
                    </div>
                    <div className={`form-input-group`}>
                        <FilterDropdown
                            ref={statusDropdownRef}
                            options={certificateStatus}
                            placeholder="Choose a Status"
                            onSelect={handleSelectStatus}
                            value={selectedStatus.name}
                        />
                    </div>
                    <div className={`form-input-group`}>
                        <SearchBarRow onSearch={(query) => setSearchQuery(query)} />
                    </div>
                    <div className={styles.clearButton} onClick={handleClearFilters}>
                        <FontAwesomeIcon icon={faCircleXmark} /> <span>Clear All</span>
                    </div>
                </div>
            </div>
            <div className={`search-card-header ${styles.donwloadBtnRow}`}>
                <div className={`form-input-row`}>
                    <div className={`form-input-group`}>
                        {(['Approved', 'Printed', 'Issued'].includes(selectedStatus.name) && selectedCourse.name !== '' && certificates.length > 0) ? (
                            <button className="btn-primary"
                                type="button"
                                onClick={() => {
                                    downloadBulk();
                                    fetchBulkCertificates();
                                }}>
                                {isBulkGenerating ? (
                                    <BeatLoader color="#FFFFFF" />
                                ) : (
                                    <>
                                        Bulk Download <FontAwesomeIcon icon={faDownload} />
                                    </>
                                )}
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>
            <div className={`search-card-body`}>
                {isCertificatesLoading ?
                    <TableSkeleton columns={["Document Ref", "Name", "Course", "Certificate Status", "Certificate Year", "Actions"]} rowCount={10} height={20} />
                    : certificates.length > 0 ?
                        <div className={`table-container`}>
                            <table className={`table`}>
                                <thead className={`thead`}>
                                    <tr>
                                        <th className={`theadColumnOne`}>Document Ref</th>
                                        <th>Name</th>
                                        <th>Course</th>
                                        <th>Certificate Status</th>
                                        <th>Certificate Year</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody className={`tbody`}>
                                    {certificates?.map((certificate) => (
                                        <tr key={certificate.id}>
                                            <td className={`tdataColumnOne`}>
                                                {certificate.document_ref}
                                            </td>
                                            <td>
                                                {certificate.student_name}
                                            </td>
                                            <td>
                                                {certificate.course.name}
                                            </td>
                                            <td>{certificate?.certificate_status}</td>
                                            <td>{certificate?.certificate_date?.split(' ')?.[2]}</td>
                                            <td className={`actionsBtn ${styles.tableBtnSec}`}>
                                                <button className={`btn-edit-label`} onClick={() => previewCertificate(certificate)}>
                                                    Preview
                                                </button>
                                                {['Approved', 'Printed', 'Issued'].includes(certificate?.certificate_status) &&
                                                    <button className={`btn-edit-secondary`} onClick={() => openCertificate(certificate)}>
                                                        {loadingCertificateId === certificate.id ? <BeatLoader color='#474747' size={5} /> : `Download`}
                                                    </button>}
                                                {['Pending'].includes(certificate?.certificate_status) &&
                                                    <button className={`btn-edit-primary`} onClick={() => {
                                                        setSelectedCertificate(certificate);
                                                        setInitialCertificate(certificate);
                                                        setCompleteDate(convertToDate(certificate.completion_date));
                                                        setCertificateDate(convertToDate(certificate.certificate_date));
                                                        setSelectedEditStatus({ id: '', name: certificate.certificate_status })
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            course_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Modify</button>

                                                }
                                                <button className={`btn-edit-label`} onClick={() => {
                                                    handleChangeStatusClick(certificate)
                                                }}>Change Status</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        :
                        <NoResultsFound />
                }
            </div>
            <div className={`search-card-footer`}>
                {pageCount > 0 && (
                    <div className={styles.paginationContainer}>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={pageCount}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Certificate Details</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Document Ref<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='document_ref'
                                                name='document_ref'
                                                value={selectedCertificate.document_ref || ''}
                                                placeholder='Enter tudent Name'
                                                className={`form-input-default`}
                                                disabled
                                            />
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Student Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='student_name'
                                                name='student_name'
                                                value={selectedCertificate.student_name || ''}
                                                onChange={handleSelectedCertificateChange}
                                                placeholder='Enter Student Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.student_name && (
                                                <p className={`text-danger`}>{editErrorMsgs.student_name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Completion Date<span className={`required-star`}>*</span>
                                            </label>
                                            <DatePicker
                                                selected={completionDate}
                                                onChange={(date) => {
                                                    setCompleteDate(date);
                                                    setErrorMsg(null);
                                                }}
                                                className={styles.dateSelector} />
                                        </div>
                                    </div>
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Certificate Date<span className={`required-star`}>*</span>
                                            </label>
                                            <DatePicker
                                                selected={certificateDate}
                                                onChange={(date) => {
                                                    setCertificateDate(date);
                                                    setErrorMsg(null);
                                                }}
                                                className={styles.dateSelector} />
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editCertificateDetails}
                                    >
                                        {isEditingCertificate ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Vehicle Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Vehicle?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Vehicle Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Vehicle?`}
                    colorType={`var(--danger)`}
                />
            )}
            {printModalOpen && <div className={`modal-overlay`}>
                <div className={`modal-card`}>
                    <div className={`modal-header`}>
                        <h4 className={`modal-title`}>Update Certificate Status</h4>
                        <FontAwesomeIcon icon={faXmark} onClick={() => setPrintModalOpen(false)} size="lg" cursor="pointer" />
                    </div>
                    <div className={`modal-body`}>
                        <div className={styles.formContainer}>
                            <form
                                className={styles.coordinatorForm}
                                noValidate
                            >
                                <div className={`form-input-row md-col`}>
                                    <div className={`form-input-group`}>
                                        <FilterDropdown
                                            ref={editStatusDropdownRef}
                                            options={higherStatuses}
                                            placeholder="Choose a Status"
                                            onSelect={handleSelectEditStatus}
                                            value={selectedEditStatus.name}
                                        />
                                    </div>
                                </div>
                                <button
                                    className={`btn-primary`}
                                    type='button'
                                    disabled={disabledEditBtn}
                                    onClick={editCertificateStatus}
                                >
                                    {isEditingCertificate ? <BeatLoader color='#ffffff' /> : `Save`}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            }
        </div>
    );
};

export default CertificateList;
